import {Howl} from 'howler';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {closePlayer, pausePlayer} from '../../../store/Slices/playerSlice';
import {RootState} from '../../../store/configstore';

const useMiniPlayer = () => {
  const dispatch = useDispatch();
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentVolume, setCurrentVolume] = useState(1.0);
  const [isMuted, setIsMuted] = useState(false);
  const soundRef = useRef<Howl | null>(null);
  const intervalRef = useRef<number | null>(null);
  const loadedSongUrl = useRef<string | null>(null);

  const {songUrl, isPlaying} = useSelector((state: RootState) => state.player);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuteState = !prev;
      soundRef.current?.mute(newMuteState);
      return newMuteState;
    });
  }, []);

  const handleVolumeChange = useCallback((_, value: number | number[]) => {
    const newVolume = value as number;
    if (!soundRef.current) return;
    setCurrentVolume(newVolume);
    soundRef.current?.volume(newVolume);
    setIsMuted(newVolume === 0);
  }, []);

  const seekTo = useCallback((time: number) => {
    if (!soundRef.current) return;
    if (soundRef.current) {
      soundRef.current.seek(time);
      setCurrentTime(time);
    }
  }, []);

  const handleClose = () => {
    soundRef.current?.stop();
    soundRef.current?.unload();
    dispatch(closePlayer());
  };

  useEffect(() => {
    if (!songUrl) {
      setCurrentTime(0);
      return;
    }
    // ✅ Prevent duplicate loading
    if (soundRef.current && loadedSongUrl.current === songUrl) {
      return;
    }
    setDuration(0);
    soundRef.current = new Howl({
      src: [songUrl],
      html5: true, // ✅ Ensure smooth seeking
      preload: true,
      autoplay: false, // ✅ Do not autoplay to avoid conflicts

      onplay: () => {
        setDuration(soundRef.current?.duration() || 0);
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = window.setInterval(() => {
          setCurrentTime(soundRef.current?.seek() || 0);
        }, 500);
      },
      onpause: () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        dispatch(pausePlayer());
      },
      onend: () => {
        setCurrentTime(0);
        dispatch(pausePlayer());
      },
      onload: () => {
        setCurrentTime(0);
      },
      onloaderror: (_, error) =>
        // eslint-disable-next-line no-console
        console.error({
          name: 'Error loading track',
          state: soundRef.current?.state(),
          error,
        }),
    });
    loadedSongUrl.current = songUrl; // ✅ Store current song URL

    return () => {
      if (soundRef.current) {
        soundRef.current.stop();
        soundRef.current.unload();
        soundRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [dispatch, songUrl]);

  useEffect(() => {
    if (isPlaying) {
      soundRef.current?.play();
    } else {
      soundRef.current?.pause();
    }
  }, [songUrl, isPlaying]);

  useEffect(() => {
    if (soundRef.current) {
      if (isPlaying) {
        // ✅ Always restart interval when playing
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = window.setInterval(() => {
          setCurrentTime(soundRef.current?.seek() || 0);
        }, 500);
      } else {
        // ✅ Clear interval when paused
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [songUrl, isPlaying]);

  return {
    currentTime,
    duration,
    currentVolume,
    isMuted,
    toggleMute,
    handleVolumeChange,
    seekTo,
    handleClose,
    soundRef,
  };
};

export default useMiniPlayer;
